import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { DEMO_BRAND, demoData } from './kiosk/demo.js'
import { KioskScreen, liveLoader } from './kiosk/KioskScreen.js'
import { OwnerLink } from './kiosk/OwnerLink.js'
import { initialLanding } from './manage/recovery.js'
import { parseRoute } from './route.js'

/**
 * Routes, without a router library (DZGO's pattern): the app has very few pages. `route.ts` says which
 * page a path is.
 * - `/kiosk/<siteId>#swd_<token>` → the hangar screen, live
 * - `/kiosk/<siteId>` with no key → the hangar screen read with the owner panel's session, for a signed-in
 *   member (the panel's "Open hangar screen"); signed out, the missing-key message
 * - `/kiosk/demo` → the hangar screen with demo data; `/kiosk/demo-fallback` with the hangar station down
 * - `/` → the live hangar screen of the test site (Skydive Testharness at Fort Morgan). Kyle, 2026-10-02:
 *   the bare address said "coming soon".
 * - anything else → "Page not found", with a link home
 * - `/manage…` → the owner screens (sign in, sites, sources, limits, hangar-screen links, settings),
 *   loaded only when asked for, so a hangar TV never downloads them.
 */

const ManageApp = lazy(() => import('./manage/ManageApp.js'))
const RecoveryApp = lazy(() => import('./manage/NewPassword.js'))
const AuthCallback = lazy(() => import('./manage/handoff.js'))
const SessionScreen = lazy(() => import('./manage/SessionScreen.js'))

/**
 * The test site's hangar screen. The key is a DISPLAY token: it can only read the fictional test site's
 * weather, nothing else, and can be revoked in one statement (dev-fixture.md). It is in the page on
 * purpose, so the front page shows live weather without a link to copy.
 */
const FRONT_PAGE_SITE = 'eae26097-1f1b-4de9-8fb2-7f581bf1039f'
const FRONT_PAGE_KEY = 'swd_liyt3MCsm4tseBxGGF_QFN5b3x8z7iczhUC1J8N9HYs'
/**
 * An installed hangar screen opens at `/kiosk/installed`: an installed app's start address cannot carry the
 * screen key (it lives after the "#"), so a screen opened from its real link keeps its own link on this
 * device and the installed app reopens it.
 */
const SAVED_SCREEN = 'skyweather.screen'
function savedScreen(): { id: string; token: string } | null {
  try {
    const v = JSON.parse(window.localStorage.getItem(SAVED_SCREEN) ?? 'null') as { id?: unknown; token?: unknown } | null
    return v && typeof v.id === 'string' && typeof v.token === 'string' && v.token.startsWith('swd_') ? { id: v.id, token: v.token } : null
  } catch {
    return null
  }
}
function saveScreen(id: string, token: string): void {
  try {
    window.localStorage.setItem(SAVED_SCREEN, JSON.stringify({ id, token }))
  } catch {
    // Private window or storage blocked: the screen still works, it just cannot be reopened from the installed app.
  }
}

export function App() {
  const [path, setPath] = useState(window.location.pathname)
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  const navigate = useCallback((to: string) => {
    window.history.pushState(null, '', to)
    setPath(window.location.pathname)
    window.scrollTo(0, 0)
  }, [])

  // A reset link, or an expired one, comes back to the front address (manage/recovery.ts).
  const [landing] = useState(initialLanding)
  useEffect(() => {
    if (landing?.kind === 'expired') {
      window.history.replaceState(null, '', '/manage')
      setPath('/manage')
    }
  }, [landing])

  const route = parseRoute(path, window.location.hash)
  let kioskId = route.page === 'kiosk' ? route.id : null
  let token = route.page === 'kiosk' ? route.token : ''
  if (kioskId === 'installed') {
    const saved = savedScreen()
    kioskId = saved?.id ?? 'installed'
    token = saved?.token ?? ''
  } else if (kioskId !== null && !kioskId.startsWith('demo') && token.startsWith('swd_')) {
    saveScreen(kioskId, token)
  }

  const load = useMemo(() => {
    if (route.page === 'front') return liveLoader(FRONT_PAGE_SITE, FRONT_PAGE_KEY)
    if (kioskId === null) return null
    if (kioskId === 'demo') return async (now: number) => demoData(now)
    if (kioskId === 'demo-fallback') return async (now: number) => demoData(now, true)
    if (kioskId === 'demo-brand') return async (now: number) => demoData(now, false, 300, DEMO_BRAND)
    if (kioskId === 'demo-expiring') return async (now: number) => demoData(now, false, 12)
    if (!token.startsWith('swd_')) return null
    return liveLoader(kioskId, token)
  }, [route.page, kioskId, token])

  if (landing?.kind === 'recovery' && path === '/') {
    return (
      <Suspense fallback={<p className="home__soon">Loading…</p>}>
        <RecoveryApp landing={landing} navigate={navigate} />
      </Suspense>
    )
  }
  if (route.page === 'callback') {
    return (
      <Suspense fallback={<p className="home__soon">Loading…</p>}>
        <AuthCallback />
      </Suspense>
    )
  }
  if (route.page === 'manage' || route.page === 'site' || route.page === 'brand' || route.page === 'join') {
    return (
      <Suspense fallback={<p className="home__soon">Loading…</p>}>
        <ManageApp route={route} navigate={navigate} />
      </Suspense>
    )
  }
  if (load) return <KioskScreen load={load} demo={kioskId?.startsWith('demo') ?? false} />
  if (route.page === 'kiosk') {
    const missingKey = (
      <main className="kiosk kiosk--message">
        <p>This hangar-screen link is missing its screen key. Copy the whole link from SkyWeather, including the part after “#”.</p>
        <OwnerLink />
      </main>
    )
    if (kioskId === null || kioskId === 'installed' || kioskId.startsWith('demo') || token !== '') return missingKey
    return (
      <Suspense fallback={<main className="kiosk kiosk--message" />}>
        <SessionScreen siteId={kioskId} fallback={missingKey} />
      </Suspense>
    )
  }
  return (
    <main className="home">
      <h1>Page not found</h1>
      <p className="home__soon">
        <a href={path.startsWith('/manage') ? '/manage' : '/'}>{path.startsWith('/manage') ? 'Locations' : 'Home'}</a>
      </p>
    </main>
  )
}
